import { useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import useUserData from 'FeatureModules/Auth/hooks/useUserData';
import { ESiteType, ISite } from 'FeatureModules/Site/types/site.types';
import { GET_SITE } from 'graphql/secure/queries/site.queries';
import React, { FC, useMemo } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import DrawerItem from './DrawerItem';
import { getDrawerMenu } from './DrawerUtils';
import { StyledList } from './StyledDrawerItems';

const PREFIX = 'DrawerMenu';

export const DRAWER_CLASSES = {
  icon: `${PREFIX}-icon`,
  subMenuIcon: `${PREFIX}-subMenuIcon`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Container = styled('div')(({ theme }) => ({
  [`& .${DRAWER_CLASSES.icon}`]: {
    flexShrink: 0,
    transition: 'transform 0.125s ease',
    transform: 'translateX(0%)',
  },

  [`& .${DRAWER_CLASSES.subMenuIcon}`]: {
    marginRight: theme.spacing(1),
  },
}));

interface IDrawerProps {
  drawerExpanded: boolean;
  hasOverflow: boolean;
}

const DrawerMenu: FC<IDrawerProps> = ({ drawerExpanded, hasOverflow }) => {
  const { id } = useParams();
  const { company } = useUserData();
  const { plan } = useCompanyPlanContext();

  const isComponentChain = !!useMatch('/component-chains/:id/*');
  const isInternalDocument = !!useMatch('/documents/internal/:id');
  const isSharedDocument = !!useMatch('/documents/shared/:id');
  const isISiteOverview = !!useMatch('/sites/:id/*');
  const isPlotsActive = !!useMatch('/sites/:id/plots/:plotId/*');

  // Display cluster sites sub menu only if selected sit is form type cluster
  const { data: siteData } = useQuery<{
    site: ISite;
  }>(GET_SITE, {
    variables: { id },
    skip: !id || !isISiteOverview,
    fetchPolicy: 'cache-first',
  });

  const isSiteCluster = siteData?.site?.siteType === ESiteType.CLUSTER;
  const isFarmSite = siteData?.site?.siteType === ESiteType.FARM;

  const MENU = useMemo(() => {
    const isCompanyDataValid: boolean =
      company?.dataValid !== undefined ? company.dataValid : false;

    return getDrawerMenu({
      plan,
      isCompanyDataValid,
      isComponentChain,
      isInternalDocument,
      isSharedDocument,
      isSiteCluster,
      isPlotsActive,
      isFarmSite,
    });
  }, [
    plan,
    company,
    isComponentChain,
    isInternalDocument,
    isPlotsActive,
    isFarmSite,
    isSharedDocument,
    isSiteCluster,
  ]);

  return (
    <Container>
      <StyledList>
        {MENU.map((item, index) => (
          <DrawerItem
            key={`${item.label}-${index}`}
            item={item}
            drawerExpanded={drawerExpanded}
            hasOverflow={hasOverflow}
          />
        ))}
      </StyledList>
    </Container>
  );
};

export default DrawerMenu;
